/**
 * Letter / word activities:
 *
 *  - word (PlayspaceWord.cs / LetterElementPlayer.cs): grey slots for each
 *    letter + shuffled letters (plus `fakeLetters` random ones). Tap a letter
 *    to fill the next slot, tap a slot to send it back. When all slots are
 *    filled the word is checked: correct → slide ends; wrong → in games the
 *    slots show "X" for 2 s and reset (error, keeps playing); in quizzes the
 *    slide ends as wrong.
 *  - phrase (PlayspacePhrase.cs): words of the phrase (+ fake words) shuffled;
 *    drag a word onto the answer line to append it (PhraseWordElement /
 *    DroppedElement; a tap appends it too), tap a placed word to send it back
 *    (PhraseWordCloneElement); "Check" compares the sequence and stays off
 *    until the answer changes.
 *  - fillWithLetters (PlayspaceFillWithLetters.cs): a question and one box per
 *    letter; typing fills the boxes, checked when complete (case-insensitive).
 */
import { useEffect, useMemo, useRef, useState } from "react";
import type { SlideOf } from "@makeit/schema";
import { ElementImage, useElementSound } from "../elements";
import { QuestionView } from "./QuizSlide";
import { COLOR_GREEN, COLOR_RED, useActivity } from "../activity";
import { shuffled } from "../util";
import { useFx } from "../fx";
import { dirOf, isRtl } from "../rtl";
import { t } from "../i18n";
import { WordChip } from "./DragActivities";
import { useDropZone } from "../drag";
import { libraryUrl } from "../../data/media";
import { alphabetFor } from "../alphabets";


// ---------------------------------------------------------------------------
// word
// ---------------------------------------------------------------------------

export function WordSlide({ slide }: { slide: SlideOf<"word"> }) {
  const activity = useActivity(1);
  const fx = useFx();
  const rootRef = useRef<HTMLDivElement>(null);
  const word = (slide.word?.text.content ?? "").trim();
  const letters = useMemo(() => {
    const alphabet = alphabetFor(word).toUpperCase();
    const extra = Array.from({ length: slide.fakeLetters }, () => alphabet[Math.floor(Math.random() * alphabet.length)]);
    return shuffled([...Array.from(word), ...extra]).map((ch, i) => ({ id: i, ch }));
  }, [slide, word]);
  const [slots, setSlots] = useState<(number | null)[]>(() => Array.from(word, () => null)); // letter ids
  const [state, setState] = useState<"playing" | "wrong" | "correct">("playing");
  const { play } = useElementSound(slide.word);
  const upper = (s: string) => s.toLocaleUpperCase();

  const tapLetter = (id: number) => {
    if (state !== "playing" || slots.includes(id)) return;
    const idx = slots.indexOf(null);
    if (idx === -1) return;
    const next = slots.slice();
    next[idx] = id;
    setSlots(next);
    if (!next.includes(null)) check(next);
  };
  const tapSlot = (i: number) => {
    if (state !== "playing" || slots[i] === null) return;
    const next = slots.slice();
    next[i] = null;
    setSlots(next);
  };
  const check = (filled: (number | null)[]) => {
    const typed = filled.map((id) => letters.find((l) => l.id === id)?.ch ?? "").join("");
    if (upper(typed) === upper(word)) {
      setState("correct");
      play();
      fx.stars(rootRef.current?.querySelector<HTMLElement>(".letter-slots"));
      activity.correct(word);
    } else {
      setState("wrong");
      activity.wrong(true, typed);
      if (!activity.isDone()) {
        window.setTimeout(() => {
          setSlots(Array.from(word, () => null));
          setState("playing");
        }, 2000);
      }
    }
  };

  const image = slide.image ?? slide.word;
  return (
    <div className="word-activity" ref={rootRef}>
      {image?.image && (
        <div className="word-image">
          <ElementImage element={image} />
        </div>
      )}
      <div className={`letter-slots count-${word.length} ${state}`} dir={dirOf(word)}>
        {slots.map((id, i) => (
          <div key={i} className={`letter-slot ${id !== null ? "filled" : ""}`} onClick={() => tapSlot(i)}>
            {state === "wrong" && !activity.quizMode ? "X" : id !== null ? letters.find((l) => l.id === id)?.ch : ""}
          </div>
        ))}
      </div>
      <div className="letter-pool" dir={dirOf(word)}>
        {letters.map((l) => (
          <div key={l.id} className="letter-tile" style={{ visibility: slots.includes(l.id) ? "hidden" : undefined }} onClick={() => tapLetter(l.id)}>
            {l.ch}
          </div>
        ))}
      </div>
      {state === "wrong" && activity.quizMode && <div className="solution-row" dir={dirOf(word)}>{word}</div>}
    </div>
  );
}

// ---------------------------------------------------------------------------
// phrase
// ---------------------------------------------------------------------------

export function PhraseSlide({ slide }: { slide: SlideOf<"phrase"> }) {
  const activity = useActivity(1);
  const fx = useFx();
  const rootRef = useRef<HTMLDivElement>(null);
  const correctWords = useMemo(() => (slide.phrase?.text.content ?? "").split(/[\n ]+/).filter(Boolean), [slide]);
  const pool = useMemo(() => shuffled([...correctWords, ...slide.phraseFakeElements.map((f) => f.text.content)].map((w, i) => ({ id: i, w }))), [slide, correctWords]);
  const [placed, setPlaced] = useState<number[]>([]);
  const [checked, setChecked] = useState<boolean[] | null>(null);
  const { play, hasSound } = useElementSound(slide.phrase);
  const answerRef = useRef<HTMLDivElement>(null);
  useDropZone("phrase-answer", answerRef, !activity.isDone());

  const add = (id: number) => {
    if (activity.isDone()) return;
    setPlaced((p) => (p.includes(id) ? p : [...p, id]));
    setChecked(null);
  };
  const remove = (id: number) => {
    if (activity.isDone()) return;
    setPlaced((p) => p.filter((x) => x !== id));
    setChecked(null);
  };
  const check = () => {
    const words = placed.map((id) => pool.find((p) => p.id === id)!.w);
    const marks = words.map((w, i) => correctWords[i] === w);
    setChecked(marks);
    const ok = marks.every(Boolean) && words.length === correctWords.length;
    if (ok) {
      if (hasSound) play();
      fx.stars(rootRef.current?.querySelector<HTMLElement>(".phrase-answer"));
      activity.correct(words.join(" "));
    } else {
      activity.wrong(true, words.join(" "));
    }
  };

  const dir = dirOf(correctWords.join(" "));
  return (
    <div className="phrase-activity" ref={rootRef}>
      <div className="phrase-answer" ref={answerRef} dir={dir}>
        {placed.length === 0 && <span className="phrase-hint">{t("dragWordsHere")}</span>}
        {placed.map((id, i) => (
          <WordChip key={id} text={pool.find((p) => p.id === id)!.w} className={checked ? (checked[i] ? "ok" : "ko") : "placed"} onClick={() => remove(id)} />
        ))}
        {hasSound && <button className="phrase-sound" onClick={play} aria-label="♪" style={{ backgroundImage: `url(${libraryUrl("images/default/volume-up-4-xxl.png")})` }} />}
      </div>
      <button className="phrase-check" disabled={placed.length === 0 || checked !== null || activity.isDone()} onClick={check}>
        {t("check")}!
      </button>
      <div className="phrase-pool" dir={dir}>
        {pool.map((p) => (
          <WordChip key={p.id} id={String(p.id)} text={p.w} hidden={placed.includes(p.id)} onClick={() => add(p.id)} onDrop={(z) => (z === "phrase-answer" && add(p.id), false)} />
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// fillWithLetters
// ---------------------------------------------------------------------------

export function FillWithLettersSlide({ slide }: { slide: SlideOf<"fillWithLetters"> }) {
  const activity = useActivity(1);
  const fx = useFx();
  const rootRef = useRef<HTMLDivElement>(null);
  const answer = (slide.fillElement?.text.content ?? "").trim();
  const [typed, setTyped] = useState("");
  const [state, setState] = useState<"playing" | "wrong" | "correct">("playing");
  const inputRef = useRef<HTMLInputElement>(null);
  const { play } = useElementSound(slide.fillElement);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);
  // the letters are always added at the end: keep the hidden input's caret there
  // (an accented letter, typed with a dead key or inserted as text, can leave it at the start)
  useEffect(() => {
    const el = inputRef.current;
    if (el && document.activeElement === el) el.setSelectionRange(typed.length, typed.length);
  }, [typed]);

  const onChange = (v: string) => {
    if (state !== "playing") return;
    const word = v.replace(/\s/g, "").slice(0, answer.length);
    setTyped(word);
    if (word.length === answer.length && answer.length > 0) {
      if (word.toLocaleUpperCase() === answer.toLocaleUpperCase()) {
        setState("correct");
        play();
        fx.stars(rootRef.current?.querySelector<HTMLElement>(".letter-slots"));
        activity.correct(word);
      } else {
        setState("wrong");
        activity.wrong(true, word);
        if (!activity.isDone()) {
          window.setTimeout(() => {
            setTyped("");
            setState("playing");
            inputRef.current?.focus();
          }, 2000);
        }
      }
    }
  };

  const hasImage = !!slide.image?.image?.fileName;
  return (
    <div className={`quiz fill-letters ${activity.quizMode ? "quiz-project" : ""} ${hasImage ? "has-image" : ""}`} ref={rootRef} onClick={() => inputRef.current?.focus()}>
      {hasImage && slide.image && (
        <div className="quiz-image">
          <ElementImage element={slide.image} />
        </div>
      )}
      <div className="quiz-question">
        <QuestionView element={slide.question} />
      </div>
      {/* PlayspaceFillWithLetters › Letters: one cell per letter (LetterFill: text best-fit ≤ 150, red line at the bottom).
          The cell waiting for a letter shows a faint "?" and a blinking caret so it reads as a text input. */}
      <div className={`letter-slots underline count-${answer.length} ${state}`} dir={dirOf(answer)}>
        {Array.from(answer).map((_, i) => {
          const active = i === typed.length && state === "playing";
          return (
            <div key={i} className={`letter-slot ${i < typed.length ? "filled" : ""} ${active ? "cursor" : ""}`} style={state !== "playing" ? { color: state === "correct" ? COLOR_GREEN : COLOR_RED } : undefined}>
              {state === "wrong" && !activity.quizMode ? "X" : (typed[i] ?? "").toLocaleUpperCase()}
              {active && <span className="letter-hint" aria-hidden>{isRtl(answer) ? "؟" : "?"}</span>}
            </div>
          );
        })}
      </div>
      {state === "wrong" && activity.quizMode && <div className="solution-row">{answer.toLocaleUpperCase()}</div>}
      <input ref={inputRef} className="hidden-input" value={typed} onChange={(e) => onChange(e.target.value)} onSelect={(e) => e.currentTarget.setSelectionRange(typed.length, typed.length)} autoComplete="off" autoCapitalize="characters" dir={dirOf(answer)} />
    </div>
  );
}
